/**
 * Centralized SEO Meta Content for Point Sails
 * 
 * This file contains all meta tags (title, description, keywords) for each page.
 * These are used by the SEOHead component to dynamically set meta tags.
 */

export interface PageMeta {
    title: string;
    description: string;
    keywords: string;
    ogType?: string;
    ogImage?: string;
    ogImageWidth?: string;
    ogImageHeight?: string;
}

export const META_CONTENT: Record<string, PageMeta> = {
    home: {
        title: 'Point Sails | Custom Replacement Sails for Sale | Buy New Boat Sails Online',
        description: 'Order custom replacement sails for your sailboat. 30+ years experience, top-tier quality, competitive pricing. Instant online quotes. Buy new sails with 5-6 week delivery.',
        keywords: 'custom sails, replacement sails for sale, buy boat sails, order sailboat sails online, new sails',
        ogType: 'website',
        ogImage: 'https://pointsails.com/images/sugar-sue-desktop.webp',
        ogImageWidth: '1920',
        ogImageHeight: '1080'
    },
    
    quoter: {
        title: 'Point Sails | Get Instant Sail Quote | Buy Custom Boat Sails Online',
        description: 'Get instant pricing on custom replacement sails for your boat. Select your boat, configure your sail, see real-time pricing. Buy quality sails online, no sales pressure.',
        keywords: 'sail quote, buy boat sails, custom sail pricing, order sails online',
        ogType: 'website'
    },
    
    cruisingSails: {
        title: 'Point Sails | Cruising Sails for Sale | Buy Durable Replacement Sails',
        description: 'Premium cruising sails for sale built for bluewater sailing. Dacron and laminate options. Custom-made for your boat. Buy online with instant pricing and expert design.',
        keywords: 'cruising sails for sale, buy cruising sails, durable boat sails, bluewater sails',
        ogType: 'website'
    },
    
    performanceSails: {
        title: 'Point Sails | Performance Racing Sails for Sale | Buy Custom Race Sails',
        description: 'High-performance racing sails for sale. Custom-designed for competitive sailing. Premium materials, expert craftsmanship. Buy performance sails online with instant quotes.',
        keywords: 'racing sails for sale, buy performance sails, competition boat sails, race sails',
        ogType: 'website'
    },
    
    downwindSails: {
        title: 'Point Sails | Downwind Sails for Sale | Buy Spinnakers & Asymmetrics',
        description: 'Custom downwind sails for sale including spinnakers, asymmetrics, and code zeros. Buy quality downwind sails online with instant pricing. Expert design and fast delivery.',
        keywords: 'downwind sails for sale, buy spinnaker, asymmetric sails, code zero sails',
        ogType: 'website'
    },
    
    codeZeroSails: {
        title: 'Point Sails | Code Zero Sails for Sale | Buy Custom Code 0 Sails Online',
        description: 'High-performance code zero sails for sale. Custom-made for light air sailing. Buy code 0 sails online with instant quotes. Premium materials and expert design.',
        keywords: 'code zero sails for sale, buy code 0 sails, code zero sailboat sails',
        ogType: 'website'
    },
    
    sailCloth: {
        title: 'Point Sails | Sail Cloth Materials Guide | Buy Quality Dacron & Laminate Sails',
        description: 'Compare sail cloth materials including Dacron, laminates, and Challenge fabrics. Learn which sailcloth is right for your boat. Buy custom sails with premium materials.',
        keywords: 'sail cloth, sailcloth materials, dacron vs laminate, Challenge sailcloth',
        ogType: 'website'
    },
    
    about: {
        title: 'Point Sails | About Us | 30+ Years Custom Sailmaking Experience',
        description: 'Point Sails has 30+ years experience designing and building custom replacement sails. Expert craftsmanship, competitive pricing, personalized service. Learn about our sailmaking process.',
        keywords: 'Point Sails, custom sailmaker, sailmaking experience',
        ogType: 'website'
    },
    
    contact: {
        title: 'Point Sails | Contact Us | Get Expert Sail Advice & Support',
        description: 'Contact Point Sails for expert advice on custom replacement sails. Our experienced team is ready to help with your sail questions. Email or call us today.',
        keywords: 'contact sailmaker, sail advice, Point Sails contact',
        ogType: 'website'
    },
    
    sailOrderingProcess: {
        title: 'Point Sails | Sail Ordering Process | How to Buy Custom Sails Online',
        description: 'Learn how to order custom replacement sails online with Point Sails. Simple 7-step process from quote to delivery. Buy quality sails with confidence.',
        keywords: 'sail ordering process, how to order sails, buy custom sails online',
        ogType: 'website'
    },
    
    privacyPolicy: {
        title: 'Point Sails | Privacy Policy',
        description: 'Point Sails privacy policy. Learn how we protect your personal information and data when you use our website and services.',
        keywords: 'privacy policy, data protection, Point Sails privacy',
        ogType: 'website'
    },
    
    // Measurement forms
    measurementMainsail: {
        title: 'Point Sails | Mainsail Measurement Form',
        description: 'Submit your mainsail measurements online. Get accurate custom mainsails built to your boat specifications. Expert design and fast delivery.',
        keywords: 'mainsail measurement, measure mainsail, custom mainsail',
        ogType: 'website'
    },
    
    measurementIMFMainsail: {
        title: 'Point Sails | IMF Mainsail Measurement Form',
        description: 'Submit your in-mast furling mainsail measurements online. Get custom IMF mainsails built to your exact specifications.',
        keywords: 'imf mainsail measurement, in-mast furling mainsail, measure imf sail',
        ogType: 'website'
    },
    
    measurementFurlingHeadsail: {
        title: 'Point Sails | Furling Headsail Measurement Form',
        description: 'Submit your furling headsail measurements online. Get custom furling genoas and jibs built to your boat specifications.',
        keywords: 'furling headsail measurement, measure furling genoa, furling jib measurement',
        ogType: 'website'
    },
    
    measurementNonFurlingHeadsail: {
        title: 'Point Sails | Non-Furling Headsail Measurement Form',
        description: 'Submit your non-furling headsail measurements online. Get custom hanked-on headsails built to your exact specifications.',
        keywords: 'headsail measurement, measure genoa, hanked-on headsail',
        ogType: 'website'
    },
    
    // Featured projects
    featuredProjects: {
        title: 'Point Sails | Featured Sail Projects | Custom Sails Gallery',
        description: 'View our featured custom sail projects. See examples of cruising sails, racing sails, and downwind sails we\'ve built for satisfied customers.',
        keywords: 'sail projects, custom sail gallery, sail examples',
        ogType: 'website'
    },
    
    // Reseller program
    resellerProgram: {
        title: 'Point Sails | Reseller Program | Partner with Us',
        description: 'Join the Point Sails reseller program. Offer quality custom replacement sails to your customers with competitive pricing and expert support.',
        keywords: 'sail reseller program, sailmaking partnership, marine dealer program',
        ogType: 'website'
    },

    // 404 page
    notFound: {
        title: 'Page Not Found | Point Sails',
        description: 'The page you are looking for does not exist. Browse our custom replacement sails or get an instant quote.',
        keywords: 'page not found, 404, Point Sails',
        ogType: 'website'
    }
};

/**
 * Default meta content if page-specific content is not found
 */
export const DEFAULT_META: PageMeta = {
    title: 'Point Sails | Custom Replacement Sails',
    description: 'Point Sails - Premium custom sailing solutions and expert sail design',
    keywords: 'custom sails, replacement sails, sailmaking, boat sails',
    ogType: 'website'
};

/**
 * Paths that should be indexed by search engines (allowlist).
 * All other SPA routes default to noindex, nofollow.
 * Comparison is case-insensitive in SEOHead.
 * Static /sails/*.html pages are NOT served through the SPA and are unaffected.
 */
export const INDEXABLE_PATHS: string[] = [
    '/',
    '/about',
    '/cruising-sails',
    '/performance-sails',
    '/downwind-sails',
    '/code-zero-sails',
    '/sail-cloth',
    '/contact',
    '/featuredprojectspage',
    '/privacy-policy',
    '/sail-ordering-process',
];
